import React, { useState, useEffect } from 'react'
import { Get } from 'api/index'
import breakpoints from 'assets/stylesheet'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import styled from 'styled-components/macro'
import SearchField from 'components/ui/SearchField'
import { useDispatch, useSelector } from 'react-redux'
import {
  toggleFilterSubmit,
  updateSelectedFilters,
  getPartnerCoverageByRegion
} from 'store/actions/filters'
import { t } from 'ttag'
import { MAX_SEARCH_WORDS, SearchableOptions } from 'store/constants/constants'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import PropTypes from 'prop-types'
import latinize from 'latinize'

const StyledFilterText = styled(FilterText)`
  width: 100%;
`
const HelpText = styled.h5`
  margin: 10px 3px !important;
  margin-top: 20px !important;
  font-style: italic;
  font-size: 12px;
  color: #4a4a4a;
  font-weight: normal;
`
const Region = styled.p`
  margin: 0 !important;
  padding: 1rem;
  border: 1px solid #aaa;
  width: 100%;
  background-color: #ffffff;
  cursor: pointer;
  border-top: none;
  &:hover {
    background-color: #284f32;
    color: white;
  }
`
const RegionContainer = styled.div`
  position: absolute;
  top: 84px;
  left: 50px;
  right: 30px;
  z-index: 100;
  max-height: 250px;
  overflow-y: auto;
  @media (max-width: ${breakpoints.bpPhonePx}) {
    left: 15px;
    right: 15px;
  }
`
const SearchFieldWrapper = styled.div`
  position: relative;
  margin-top: 19px;
`
const SearchIcon = styled.i`
  font-size: 25px;
  color: #9faee5;
  cursor: pointer;
  position: absolute;
  top: 8px;
  right: 9px;
`
const SpinnerContainer = styled.div`
  min-height: 40px;
  position: relative;
`
const ErrorMessage = styled.span`
  color: red;
  font-size: 12px;
  margin-left: 4px;
  display: block;
`
const SearchStringContainer = styled.p`
  margin-top: 7px;
`
function ConditionalFreeTextSearch({ isSummary, applySearch, submitEnabled }) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const searchFromFields = filters.selectedFilters[currentSearchable].search.fields

  const searchStringValues = filters.selectedFilters[currentSearchable].search.list
  const searchStringType = filters.selectedFilters[currentSearchable].search.type
  const { rawSearchString } = filters.selectedFilters[currentSearchable].search
  const currentFilters = filters.selectedFilters[currentSearchable]
  return (
    <PureConditionalFreeTextSearch
      isSummary={isSummary}
      applySearch={applySearch}
      searchFromFields={searchFromFields}
      searchStringValues={searchStringValues}
      searchStringType={searchStringType}
      rawSearchString={rawSearchString}
      currentSearchable={currentSearchable}
      currentFilters={currentFilters}
      isRegionsPending={filters.isRegionsPending}
      submitEnabled={submitEnabled}
    />
  )
}

const PureConditionalFreeTextSearch = React.memo(function PureConditionalFreeTextSearch({
  isSummary,
  applySearch,
  searchFromFields,
  searchStringValues,
  searchStringType,
  rawSearchString,
  currentSearchable,
  currentFilters,
  isRegionsPending,
  submitEnabled
}) {
  const dispatch = useDispatch()
  const [regions, setRegions] = useState([])
  const [filteredRegions, setFilteredRegions] = useState([])
  useEffect(() => {
    const fetchRegions = async () => {
      if (regions.length) {
        return
      }

      const data = await Get('/api/search/regions/', { geometry: false })
      setRegions(data)
    }
    if (currentSearchable === 'countries_and_regions') {
      fetchRegions()
    }
  }, [currentSearchable, regions.length])

  const [searchString, setSearchString] = useState(rawSearchString)
  const [syntaxError, setSyntaxError] = useState(false)
  const [lengthError, setLengthError] = useState(false)
  const syntaxErrorMessage = t`Use only one search modifier (AND or OR)`
  const lengthErrorMessage = t`The number of search words is limited to ${MAX_SEARCH_WORDS}`

  const selectRegion = async region => {
    const oldRegions = currentFilters.options.regions
    const oldNuts = currentFilters.programmes.nuts.map(n => n.id)
    if (oldNuts.includes(region.nuts_id)) {
      setFilteredRegions([])
      setSearchString('')
      return
    }
    const newRegion = await Get('/api/search/regions/', {
      name: region.nuts_id,
      with_links: true,
      with_child_ids: true
    })
    const data = {
      filterValue: [
        ...oldRegions,
        {
          id: newRegion[0].nuts_id,
          label: newRegion[0].name,
          paths: newRegion[0].paths,
          type: newRegion[0].type,
          db_id: newRegion[0].id,
          children: newRegion[0].children,
          english_alias: newRegion[0].english_alias,
          links: newRegion[0].links
        }
      ],
      filterType: 'options',
      filterId: 'regions'
    }
    const data2 = {
      filterValue: [...oldNuts, newRegion[0].nuts_id].map(n => ({ id: n })),
      filterType: 'programmes',
      filterId: 'nuts'
    }
    dispatch(updateSelectedFilters(data))
    dispatch(updateSelectedFilters(data2))
    dispatch(
      getPartnerCoverageByRegion({
        nuts: [...oldNuts, newRegion[0].nuts_id]
      })
    )
    setFilteredRegions([])
    setSearchString('')
  }
  function renderSearchString() {
    if (syntaxError) {
      return <ErrorMessage>{syntaxErrorMessage}</ErrorMessage>
    } else if (lengthError) {
      return <ErrorMessage>{lengthErrorMessage}</ErrorMessage>
    }
    if (searchStringType === 'or' || searchStringType === 'and') {
      if (searchStringValues instanceof Array) {
        return searchStringValues.map(v => (
          <SelectedFilterContainer className="selected-filters" key={v}>
            <SelectedFilter>
              <StyledFilterText>
                <span>{v}</span>
              </StyledFilterText>
            </SelectedFilter>
          </SelectedFilterContainer>
        ))
      }
    }
    return false
  }

  function handleSearchChange(event) {
    const origString = event.target.value
    let splitString = ''
    let searchType = null
    setSyntaxError(false)
    dispatch(
      toggleFilterSubmit({
        submitEnabled: true
      })
    )
    if (origString.includes(' OR ')) {
      splitString = origString.split(' OR ')
      searchType = 'or'
      setSyntaxError(false)
      const submitData = {
        submitEnabled: true
      }
      dispatch(toggleFilterSubmit(submitData))
    }
    if (origString.includes(' AND ')) {
      splitString = origString.split(' AND ')
      searchType = 'and'
      setSyntaxError(false)
      const submitData = {
        submitEnabled: true
      }
      dispatch(toggleFilterSubmit(submitData))
    }
    if (origString.includes(' AND ') && origString.includes(' OR ')) {
      searchType = 'exact'
      setSyntaxError(true)
      const submitData = {
        submitEnabled: false
      }
      dispatch(toggleFilterSubmit(submitData))
    }
    if (origString === '') {
      setSyntaxError(false)
      const submitData = {
        submitEnabled: true
      }
      dispatch(toggleFilterSubmit(submitData))
    }
    setSearchString(origString)
    if (currentSearchable === 'countries_and_regions') {
      const newRegions = regions.filter(r => {
        const regionString = r.english_alias
          ? `${r.english_alias} / ${r.name} [${r.nuts_id}]`
          : `${r.name} [${r.nuts_id}]`
        return latinize(regionString).toLowerCase().indexOf(origString.toLowerCase()) > -1
      })
      setFilteredRegions(newRegions)
      return
    }
    if (splitString.length > MAX_SEARCH_WORDS) {
      setLengthError(true)
      const submitData = {
        submitEnabled: false
      }
      dispatch(toggleFilterSubmit(submitData))
    } else {
      setLengthError(false)
      const submitData = {
        submitEnabled: true
      }
      dispatch(toggleFilterSubmit(submitData))
    }

    let list = searchType === null ? [origString] : splitString
    list = list.filter(s => s !== '')
    const resultingValue = {
      list,
      type: searchType,
      fields: searchFromFields,
      rawSearchString: event.target.value
    }
    const data = {
      filterType: 'search',
      filterId: 'search',
      filterValue: resultingValue,
      isHigherLevelFilter: true
    }
    dispatch(updateSelectedFilters(data))
  }

  function renderSummary() {
    if (rawSearchString !== '') {
      return (
        <SelectedFilterContainer className="selected-filters">
          <SelectedFilter>
            <StyledFilterText>
              <span>{rawSearchString}</span>
            </StyledFilterText>
          </SelectedFilter>
        </SelectedFilterContainer>
      )
    }
    return null
  }

  function getPlaceholder() {
    switch (currentSearchable) {
      case SearchableOptions.PROJECTS:
        return t`Search keep.eu...`
      case SearchableOptions.PROGRAMMES:
        return t`Type any part of the name of the programme(s)`
      case SearchableOptions.PARTNERS:
        return t`Type any part of partner name`
      case SearchableOptions.COUNTRIES_AND_REGIONS:
        return t`Start typing country/region name and select offered options`
      default:
        return null
    }
  }

  function handleOnKeyDown(e) {
    if (e.key === 'Enter' && submitEnabled) {
      applySearch()
    }
  }

  function handleSearchIconClick() {
    if (submitEnabled) {
      applySearch()
    }
  }

  if (currentSearchable === 'countries_and_regions' && isRegionsPending) {
    return (
      <SpinnerContainer>
        <LoadingSpinner showText />
      </SpinnerContainer>
    )
  }
  return (
    <>
      {isSummary ? (
        renderSummary()
      ) : (
        <>
          <SearchFieldWrapper>
            <SearchField
              onChange={handleSearchChange}
              value={searchString}
              placeholder={getPlaceholder()}
              onKeyDown={handleOnKeyDown}
              aria-label={t`Search`}
              hideIcon
            />
            <SearchIcon onClick={handleSearchIconClick} className="far fa-search" />
          </SearchFieldWrapper>
          {renderSearchString() ? (
            <SearchStringContainer>{renderSearchString()}</SearchStringContainer>
          ) : null}
          {currentSearchable === 'projects' && (
            <HelpText>Easy to use tools to improve your text search</HelpText>
          )}
          {currentSearchable === 'countries_and_regions' && searchString.length > 1 && (
            <RegionContainer>
              {filteredRegions.map(r => (
                <Region key={r.id} onClick={() => selectRegion(r)}>
                  {r.english_alias
                    ? `${r.english_alias} / ${r.name} [${r.nuts_id}]`
                    : `${r.name} [${r.nuts_id}]`}
                </Region>
              ))}
            </RegionContainer>
          )}
        </>
      )}
    </>
  )
})

ConditionalFreeTextSearch.propTypes = {
  isSummary: PropTypes.bool,
  applySearch: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  submitEnabled: PropTypes.bool
}

PureConditionalFreeTextSearch.propTypes = {
  isSummary: PropTypes.bool,
  applySearch: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  searchFromFields: PropTypes.array,
  searchStringValues: PropTypes.array,
  searchStringType: PropTypes.string,
  rawSearchString: PropTypes.string,
  currentSearchable: PropTypes.string,
  currentFilters: PropTypes.object,
  isRegionsPending: PropTypes.bool,
  submitEnabled: PropTypes.bool
}

export default ConditionalFreeTextSearch
